import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { normalize } from "@/lib/resume";

const diff = (a, b) => {
  const bullets = (r) => r.experience.reduce((n, e) => n + e.bullets.filter((x) => x.trim()).length, 0);
  const added = b.skills.filter((s) => !a.skills.map((x) => x.toLowerCase()).includes(s.toLowerCase()));
  return [
    a.summary !== b.summary && { k: "Summary", before: a.summary || "—", after: b.summary || "—" },
    { k: "Experience bullets", before: `${bullets(a)} bullets`, after: `${bullets(b)} bullets` },
    added.length > 0 && { k: "New skills", before: `${a.skills.length} skills`, after: `+ ${added.slice(0, 8).join(", ")}${added.length > 8 ? "…" : ""}` },
  ].filter(Boolean);
};

export const OptimizedReview = ({ optimized, current, onUse, onKeep }) => {
  const [next, setNext] = useState(optimized.resume ? normalize(optimized.resume) : null);
  const [err, setErr] = useState(null);
  const [try_, setTry] = useState(0);
  useEffect(() => {
    if (next) return;
    setErr(null);
    api.post("/resume-builder/parse", { text: optimized.text }).then(({ data }) => setNext(normalize(data?.resume || data))).catch((e) => setErr(errMsg(e)));
  }, [try_]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = next && diff(current || normalize({}), next);
  return (
    <div className="fade-up mb-5 rounded-2xl border border-[#D4AF37]/50 bg-[#141417] p-4 sm:p-5" data-testid="optimized-review">
      <div className="mb-3 flex items-center gap-2"><Wand2 className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-base text-zinc-100 md:text-lg">Optimized version for this job</h3></div>
      {err ? <p className="text-sm text-red-400" data-testid="optimized-review-error">{err} <button onClick={() => setTry((n) => n + 1)} className="ml-1 font-medium text-[#F3C649] underline" data-testid="optimized-retry">Retry</button></p>
        : !next ? <p className="flex items-center gap-2 text-sm text-zinc-400"><Spinner className="text-[#D4AF37]" />Preparing the optimized version…</p>
        : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.k} className="grid gap-2 rounded-lg border border-[#27272A] bg-[#09090B] p-3 text-xs sm:grid-cols-[120px_minmax(0,1fr)_16px_minmax(0,1fr)] sm:items-start" data-testid={`optimized-diff-${r.k.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="font-medium uppercase tracking-wider text-zinc-500">{r.k}</span>
                <span className="line-clamp-3 break-words text-zinc-400">{r.before}</span>
                <ArrowRight className="hidden h-4 w-4 text-[#D4AF37] sm:block" />
                <span className="line-clamp-3 break-words text-emerald-300">{r.after}</span>
              </div>
            ))}
          </div>
        )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => { onUse(next); toast.success("Optimized version loaded. Review and save when ready."); }} disabled={!next} data-testid="optimized-use"><Check className="h-4 w-4" />Use it</Button>
        <Button size="sm" variant="ghost" onClick={onKeep} data-testid="optimized-keep"><X className="h-4 w-4" />Keep mine</Button>
      </div>
      <p className="mt-2 text-[11px] text-zinc-600">Your current main resume stays safe as a saved version when you tap "Save as my resume".</p>
    </div>
  );
};
